import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Alert,
  Badge
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { 
  chatWithTask,
  getTaskConversations,
  getTaskConversation,
  createTaskConversation,
  addMessageToTaskConversation,
  updateTaskConversationTitle,
  archiveTaskConversation,
  deleteTaskConversation
} from '../../api';

const TaskChatManager = ({ task, agent, appId, onClose }) => {
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Conversation management
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [showConversations, setShowConversations] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedConversationForMenu, setSelectedConversationForMenu] = useState(null);

  // Load conversations on component mount
  useEffect(() => {
    if (task?._id) {
      loadTaskConversations();
    }
  }, [task?._id]);

  const loadTaskConversations = async () => {
    try {
      const response = await getTaskConversations(task._id);
      setConversations(response.data.data || []);
    } catch (err) {
      console.error('Error loading task conversations:', err);
      setError('Failed to load task conversations');
    }
  };

  const handleLoadConversation = async (conversation) => {
    try {
      const response = await getTaskConversation(conversation._id);
      setCurrentConversation(response.data.data);
      setChatHistory(response.data.data.messages || []);
      setShowConversations(false);
    } catch (err) {
      console.error('Error loading task conversation:', err);
      setError('Failed to load task conversation');
    }
  };

  const handleCreateNewConversation = async () => {
    try {
      const conversationData = {
        taskId: task._id,
        title: newConversationTitle || `Task: ${task.name}`,
        description: `Discussion about ${task.name}`,
        taskContext: {
          taskName: task.name,
          taskDescription: task.description,
          taskStatus: task.status,
          taskPriority: task.priority,
          taskCategory: task.category
        }
      };
      
      const response = await createTaskConversation(conversationData, appId);
      const newConversation = response.data.data;
      
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
      setChatHistory([]);
      setNewConversationTitle('');
      setShowNewConversation(false);
      setShowConversations(false);
    } catch (err) {
      console.error('Error creating task conversation:', err);
      setError('Failed to create task conversation');
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !task) return;

    const userMessage = {
      role: 'user',
      content: chatInput,
      timestamp: new Date()
    };

    const updatedChatHistory = [...chatHistory, userMessage];
    setChatHistory(updatedChatHistory);
    setChatInput('');
    setIsLoading(true);

    try {
      // Send message with conversation context
      const response = await chatWithTask(task._id, updatedChatHistory, appId, currentConversation?._id);
      
      const agentResponse = {
        role: 'assistant',
        content: response.data.data.reply,
        timestamp: new Date()
      };
      
      const finalChatHistory = [...updatedChatHistory, agentResponse];
      setChatHistory(finalChatHistory);

      // Update current conversation if it was created
      if (response.data.data.conversationId && !currentConversation) {
        setCurrentConversation({
          _id: response.data.data.conversationId,
          title: response.data.data.conversationTitle || `Task: ${task.name}`
        });
      }

      // Show task information if updated
      if (response.data.data.task) {
        console.log('Task updated:', response.data.data.task);
      }
    } catch (err) {
      console.error('Error chatting with task:', err);
      const errorResponse = {
        role: 'assistant',
        content: 'Sorry, I encountered an error while discussing this task. Please try again.',
        timestamp: new Date()
      };
      setChatHistory([...updatedChatHistory, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConversationMenu = (event, conversation) => {
    setMenuAnchor(event.currentTarget);
    setSelectedConversationForMenu(conversation);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedConversationForMenu(null);
  };

  const handleEditTitle = async () => {
    if (!selectedConversationForMenu) return;
    
    const newTitle = prompt('Enter new title:', selectedConversationForMenu.title);
    if (newTitle && newTitle !== selectedConversationForMenu.title) {
      try {
        await updateTaskConversationTitle(selectedConversationForMenu._id, newTitle);
        setConversations(prev => 
          prev.map(conv => 
            conv._id === selectedConversationForMenu._id 
              ? { ...conv, title: newTitle }
              : conv
          )
        );
        if (currentConversation?._id === selectedConversationForMenu._id) {
          setCurrentConversation(prev => ({ ...prev, title: newTitle }));
        }
      } catch (err) {
        console.error('Error updating title:', err);
        setError('Failed to update title');
      }
    }
    handleCloseMenu();
  };

  const handleArchiveConversation = async () => {
    if (!selectedConversationForMenu) return;
    
    try {
      await archiveTaskConversation(selectedConversationForMenu._id);
      setConversations(prev => 
        prev.filter(conv => conv._id !== selectedConversationForMenu._id)
      );
      if (currentConversation?._id === selectedConversationForMenu._id) {
        setCurrentConversation(null);
        setChatHistory([]);
      }
    } catch (err) {
      console.error('Error archiving conversation:', err);
      setError('Failed to archive conversation');
    }
    handleCloseMenu();
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversationForMenu) return;
    
    if (window.confirm('Are you sure you want to delete this task conversation? This action cannot be undone.')) {
      try {
        await deleteTaskConversation(selectedConversationForMenu._id);
        setConversations(prev => 
          prev.filter(conv => conv._id !== selectedConversationForMenu._id)
        );
        if (currentConversation?._id === selectedConversationForMenu._id) {
          setCurrentConversation(null);
          setChatHistory([]);
        }
      } catch (err) {
        console.error('Error deleting conversation:', err);
        setError('Failed to delete conversation');
      }
    }
    handleCloseMenu();
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon />
            <Typography variant="h6">
              Task Chat: {task?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Task Conversation History">
              <IconButton 
                size="small" 
                onClick={() => setShowConversations(true)}
              >
                <Badge badgeContent={conversations.length} color="primary">
                  <HistoryIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="New Task Conversation">
              <IconButton 
                size="small" 
                onClick={() => setShowNewConversation(true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {currentConversation && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body2" color="primary.contrastText">
              Current: {currentConversation.title}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ height: 400, overflowY: 'auto', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          {chatHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Start a conversation about this task. Ask questions, get updates, or discuss progress.
              </Typography>
            </Box>
          ) : (
            chatHistory.map((message, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                    color: message.role === 'user' ? 'white' : 'text.primary',
                    border: message.role === 'assistant' ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2">
                    {message.content}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'white', border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {agent?.name || 'Agent'} is thinking about {task?.name}...
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Ask about this task..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={isLoading}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!chatInput.trim() || isLoading}
          >
            Send
          </Button>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Task Conversations Dialog */}
      <Dialog 
        open={showConversations} 
        onClose={() => setShowConversations(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Task Conversation History</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setShowConversations(false);
                setShowNewConversation(true);
              }}
            >
              New
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {conversations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FolderIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No task conversations yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start a new conversation to begin discussing {task?.name}.
              </Typography>
            </Box>
          ) : (
            <List>
              {conversations.map((conversation) => (
                <ListItem 
                  key={conversation._id} 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1, 
                    mb: 1 
                  }}
                  onClick={() => handleLoadConversation(conversation)}
                >
                  <ListItemIcon>
                    <ChatIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={conversation.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {conversation.description || 'No description'}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip label={conversation.taskContext?.taskStatus || 'unknown'} size="small" />
                          <Typography variant="caption" color="text.secondary">
                            {conversation.performance?.totalMessages || 0} messages
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(conversation.performance?.lastActivity || conversation.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <IconButton 
                    size="small"
                    onClick={(e) => handleConversationMenu(e, conversation)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConversations(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Task Conversation Dialog */}
      <Dialog 
        open={showNewConversation} 
        onClose={() => setShowNewConversation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start New Task Conversation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Conversation Title"
            value={newConversationTitle}
            onChange={(e) => setNewConversationTitle(e.target.value)}
            placeholder={`Task: ${task?.name}`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewConversation(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateNewConversation} 
            variant="contained"
          >
            Create Conversation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleEditTitle}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Title
        </MenuItem>
        <MenuItem onClick={handleArchiveConversation}>
          <ArchiveIcon sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteConversation} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default TaskChatManager; 